import { useCallback, useEffect, useState } from "react";
import { GetRewardsCatalog, RewardCatalogEntry } from "../firebase-config";
import { normalizeRewardCollection, NormalizedReward } from "../utils/rewards";

let catalogCache: RewardCatalogEntry[] | null = null;
let pendingCatalogRequest: Promise<RewardCatalogEntry[]> | null = null;

const loadCatalogRecords = async (
  forceRefresh = false,
): Promise<RewardCatalogEntry[]> => {
  if (!forceRefresh && catalogCache) {
    return catalogCache;
  }

  if (!forceRefresh && pendingCatalogRequest) {
    return pendingCatalogRequest;
  }

  pendingCatalogRequest = GetRewardsCatalog()
    .then((catalogRecords) => {
      catalogCache = catalogRecords;
      pendingCatalogRequest = null;
      return catalogRecords;
    })
    .catch((error) => {
      pendingCatalogRequest = null;
      throw error;
    });

  return pendingCatalogRequest;
};

export const invalidateRewardsCatalog = () => {
  catalogCache = null;
  pendingCatalogRequest = null;
};

// Devuelve el catálogo de recompensas ya normalizado (NormalizedReward[]),
// listo para usar con mergeRewardsWithUserState() o pasar directo a
// RewardedProfileDisplay. Se cachea en memoria para no repetir la lectura
// a Firestore en cada pantalla que muestra el avatar con insignias.
const useRewardsCatalog = () => {
  const [catalog, setCatalog] = useState<NormalizedReward[]>(() =>
    normalizeRewardCollection(catalogCache ?? []),
  );
  const [isLoading, setIsLoading] = useState(false);

  const refreshCatalog = useCallback(async () => {
    setIsLoading(true);

    try {
      const catalogRecords = await loadCatalogRecords(true);
      const normalized = normalizeRewardCollection(catalogRecords);
      setCatalog(normalized);
      return normalized;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (catalogCache) {
      setCatalog(normalizeRewardCollection(catalogCache));
    }

    void refreshCatalog();
  }, [refreshCatalog]);

  return { catalog, isLoading, refreshCatalog };
};

export default useRewardsCatalog;
